/**
 * What the game sounds like, as data and arithmetic — with no Web Audio in it.
 *
 * Everything here is pure, for the same reason `camera.ts` is: the interesting
 * decisions in this feature are *policy* — which cue a weapon gets, which of
 * three recordings a gun of that weight should use, how long its tail may sound
 * before the next round is due, how loud a shot 90 m away should be, and how
 * many overlapping reports a scattergun is allowed — and none of them need an
 * `AudioContext` to be true. `synth.ts` turns a `VoiceSpec` into oscillators and
 * `samples.ts` turns a `SampleSpec` into a buffer source; this file decides
 * which, how loud, and whether it gets to play at all.
 *
 * The split is what makes the voice cap testable in a plain Node test run. The
 * unit suite has no DOM and no audio device, and the two rules that matter most
 * here — that a scattergun must not produce eight full-volume cannon reports,
 * and that a shot must not outlive its own reload — are exactly the kind of
 * thing that must not rely on somebody listening to them.
 */

// ─── Cues ────────────────────────────────────────────────────────────────────

import type { WeaponArchetype } from '@shared/weapons';

/**
 * Every distinct noise the game can make.
 *
 * Firing is keyed by archetype rather than by turret id: a turret is a set of
 * numbers, an archetype is a *behaviour*, and the behaviour is what the ear is
 * actually listening to. Eight turrets sharing the `single` archetype should
 * sound alike; a scattergun and a railgun must not.
 */
export type CueId =
  | `fire:${WeaponArchetype}`
  | 'impact:ground'
  | 'impact:wall'
  | 'impact:tank'
  | 'hit'
  | 'reload'
  | 'destroyed'
  | 'blast'
  | 'airdrop:incoming'
  | 'airdrop:land'
  | 'airdrop:claim'
  | 'ui:click';

/**
 * One synthesised sound, described as parameters rather than as a recording.
 *
 * Three layers, because three is what it takes to cover this game's palette
 * with one code path: a pitched **body** that sweeps (the boom of a gun, the
 * thud of a crate), a **noise** transient through a filter (the crack, the
 * dirt, the chuff of a scattergun), and an inharmonic **ring** that outlasts
 * both (metal being struck). A cue that wants none of a layer sets its gain to
 * 0 and pays nothing — no node is built for it.
 */
export interface VoiceSpec {
  /** Peak gain before distance, stacking and the master volume. 0–1. */
  gain: number;
  /**
   * Total lifetime in seconds.
   *
   * Load-bearing beyond the envelope: the limiter uses it to know when the
   * voice frees up again, so a cue that lies about its length either hogs the
   * cap or is counted as finished while it is still sounding.
   */
  duration: number;
  /** Seconds to peak. A hard attack reads as a crack, a soft one as a whoomp. */
  attack: number;

  /** Pitched body: sweeps from `bodyFrom` to `bodyTo` Hz as it decays. */
  bodyWave: OscillatorType;
  bodyFrom: number;
  bodyTo: number;
  bodyGain: number;
  bodyDecay: number;

  /** Filtered noise transient. `noiseQ` at 0 selects a lowpass, above it a bandpass. */
  noiseGain: number;
  noiseDecay: number;
  noiseHz: number;
  noiseQ: number;

  /** Inharmonic partials that sustain past the transient. 0 gain builds nothing. */
  ringHz: number;
  ringGain: number;
  ringDecay: number;
}

function voice(over: Partial<VoiceSpec>): VoiceSpec {
  return {
    gain: 0.8,
    duration: 0.6,
    attack: 0.001,
    bodyWave: 'sine',
    bodyFrom: 160,
    bodyTo: 50,
    bodyGain: 0.8,
    bodyDecay: 0.3,
    noiseGain: 0.6,
    noiseDecay: 0.12,
    noiseHz: 1600,
    noiseQ: 0,
    ringHz: 0,
    ringGain: 0,
    ringDecay: 0.2,
    ...over,
  };
}

/**
 * The catalogue.
 *
 * The eight firing cues are deliberately spread across the whole parameter
 * space rather than being one boom at eight pitches. Read down them: `scatter`
 * is almost entirely noise and has barely any body, `rail` is a bright
 * descending sweep with a long metallic ring and almost no low end, `mortar`
 * has a soft attack and no crack at all. Those are different *shapes*, which is
 * what makes them tell apart in a firefight; pitch alone does not survive three
 * tanks shooting at once.
 */
/**
 * The eight firing cues as *designed*, before `UNIFORM_FIRE_ARCHETYPE` collapses
 * them. Exported so a test can assert the distinct shapes still exist and would
 * come back the moment the override is turned off.
 */
export const CUE_SHAPES: Record<CueId, VoiceSpec> = {
  // The plain gun: a deep report with a hard crack on the front.
  'fire:single': voice({
    gain: 0.9,
    duration: 0.75,
    bodyFrom: 150,
    bodyTo: 42,
    bodyGain: 0.85,
    bodyDecay: 0.34,
    noiseGain: 0.7,
    noiseDecay: 0.13,
    noiseHz: 1500,
  }),
  // Parallel barrels: tighter and higher, so a twin reads as a lighter gun.
  'fire:multi': voice({
    gain: 0.8,
    duration: 0.55,
    bodyFrom: 195,
    bodyTo: 62,
    bodyGain: 0.7,
    bodyDecay: 0.22,
    noiseGain: 0.75,
    noiseDecay: 0.1,
    noiseHz: 2200,
    ringHz: 1400,
    ringGain: 0.12,
    ringDecay: 0.16,
  }),
  // Burst: dry and snappy. Three of these arrive four ticks apart, which is
  // well outside the coalescing window, so all three are heard as a rhythm.
  'fire:burst': voice({
    gain: 0.7,
    duration: 0.4,
    bodyFrom: 260,
    bodyTo: 90,
    bodyGain: 0.55,
    bodyDecay: 0.15,
    noiseGain: 0.7,
    noiseDecay: 0.07,
    noiseHz: 3000,
  }),
  // Capacitor discharge: electrical rather than explosive, with a tail.
  'fire:charge': voice({
    gain: 0.95,
    duration: 1,
    bodyWave: 'sawtooth',
    bodyFrom: 420,
    bodyTo: 38,
    bodyGain: 0.6,
    bodyDecay: 0.45,
    noiseGain: 0.5,
    noiseDecay: 0.2,
    noiseHz: 3200,
    noiseQ: 3,
    ringHz: 1250,
    ringGain: 0.3,
    ringDecay: 0.5,
  }),
  // Scattergun: broadband chuff, almost no body. The odd one out on purpose.
  'fire:scatter': voice({
    gain: 0.8,
    duration: 0.45,
    bodyFrom: 130,
    bodyTo: 70,
    bodyGain: 0.28,
    bodyDecay: 0.14,
    noiseGain: 0.95,
    noiseDecay: 0.22,
    noiseHz: 2600,
  }),
  // Mortar: a soft-edged whoomp with the crack taken off the front.
  'fire:mortar': voice({
    gain: 0.85,
    duration: 0.9,
    attack: 0.014,
    bodyFrom: 110,
    bodyTo: 34,
    bodyGain: 0.9,
    bodyDecay: 0.42,
    noiseGain: 0.25,
    noiseDecay: 0.16,
    noiseHz: 700,
  }),
  // Ricochet gun: a metallic crack that rings after it.
  'fire:ricochet': voice({
    gain: 0.8,
    duration: 0.7,
    bodyFrom: 210,
    bodyTo: 80,
    bodyGain: 0.6,
    bodyDecay: 0.18,
    noiseGain: 0.65,
    noiseDecay: 0.09,
    noiseHz: 3400,
    noiseQ: 2,
    ringHz: 2400,
    ringGain: 0.28,
    ringDecay: 0.35,
  }),
  // Railgun: bright, electric and long. Shares nothing with the scattergun.
  'fire:rail': voice({
    gain: 0.9,
    duration: 1.1,
    bodyWave: 'sawtooth',
    bodyFrom: 900,
    bodyTo: 70,
    bodyGain: 0.5,
    bodyDecay: 0.18,
    noiseGain: 0.5,
    noiseDecay: 0.14,
    noiseHz: 5200,
    noiseQ: 4,
    ringHz: 3200,
    ringGain: 0.34,
    ringDecay: 0.55,
  }),

  // ─── Shells arriving ───────────────────────────────────────────────────────
  'impact:ground': voice({
    gain: 0.45,
    duration: 0.35,
    bodyFrom: 90,
    bodyTo: 38,
    bodyGain: 0.7,
    bodyDecay: 0.18,
    noiseGain: 0.55,
    noiseDecay: 0.12,
    noiseHz: 900,
  }),
  'impact:wall': voice({
    gain: 0.5,
    duration: 0.4,
    bodyFrom: 140,
    bodyTo: 55,
    bodyGain: 0.6,
    bodyDecay: 0.14,
    noiseGain: 0.6,
    noiseDecay: 0.1,
    noiseHz: 1800,
    ringHz: 900,
    ringGain: 0.14,
    ringDecay: 0.2,
  }),
  // Metal on metal. The ring is what distinguishes hitting a tank from hitting
  // the floor beside it, which is the one distinction a gunner actually wants.
  'impact:tank': voice({
    gain: 0.6,
    duration: 0.5,
    bodyFrom: 180,
    bodyTo: 120,
    bodyGain: 0.4,
    bodyDecay: 0.08,
    noiseGain: 0.7,
    noiseDecay: 0.07,
    noiseHz: 3800,
    noiseQ: 3,
    ringHz: 1750,
    ringGain: 0.4,
    ringDecay: 0.3,
  }),

  /**
   * Being hit yourself.
   *
   * A separate cue rather than `impact:tank` played nearby, and louder and
   * lower than it: from inside the hull the interesting information is *that
   * you were hit*, and it has to cut through your own gun. Always played at the
   * listener with no distance rolloff and no pan — the shell is already here.
   */
  hit: voice({
    gain: 0.95,
    duration: 0.85,
    bodyFrom: 140,
    bodyTo: 45,
    bodyGain: 0.75,
    bodyDecay: 0.3,
    noiseGain: 0.6,
    noiseDecay: 0.1,
    noiseHz: 2200,
    noiseQ: 2,
    ringHz: 620,
    ringGain: 0.45,
    ringDecay: 0.55,
  }),

  /**
   * The breech closing on the next round.
   *
   * Synthesised here as the fallback for `reload.webm`: a short mechanical
   * double-knock, all transient and no boom, with a little ring so it reads as
   * metal rather than as a thump on a box. Quiet on purpose — it fires once per
   * reload for the whole match and must sit under the gun rather than beside
   * it. Never positioned: it is your own breech.
   */
  reload: voice({
    gain: 0.34,
    duration: 0.22,
    bodyFrom: 320,
    bodyTo: 90,
    bodyGain: 0.35,
    bodyDecay: 0.07,
    noiseGain: 0.5,
    noiseDecay: 0.05,
    noiseHz: 2600,
    noiseQ: 1.6,
    ringHz: 1900,
    ringGain: 0.16,
    ringDecay: 0.12,
  }),

  // ─── Destruction ───────────────────────────────────────────────────────────
  destroyed: voice({
    gain: 1,
    duration: 1.8,
    attack: 0.004,
    bodyFrom: 120,
    bodyTo: 24,
    bodyGain: 0.95,
    bodyDecay: 0.9,
    noiseGain: 0.95,
    noiseDecay: 0.75,
    noiseHz: 700,
  }),
  blast: voice({
    gain: 0.8,
    duration: 1.1,
    attack: 0.003,
    bodyFrom: 130,
    bodyTo: 30,
    bodyGain: 0.85,
    bodyDecay: 0.55,
    noiseGain: 0.8,
    noiseDecay: 0.45,
    noiseHz: 900,
  }),

  // ─── Airdrops ──────────────────────────────────────────────────────────────
  // A descending whistle as the crate is released, so a drop announces itself.
  'airdrop:incoming': voice({
    gain: 0.3,
    duration: 1.3,
    attack: 0.08,
    bodyWave: 'triangle',
    bodyFrom: 900,
    bodyTo: 380,
    bodyGain: 0.8,
    bodyDecay: 0.9,
    noiseGain: 0.1,
    noiseDecay: 0.6,
    noiseHz: 1200,
  }),
  'airdrop:land': voice({
    gain: 0.55,
    duration: 0.5,
    bodyFrom: 100,
    bodyTo: 40,
    bodyGain: 0.8,
    bodyDecay: 0.22,
    noiseGain: 0.6,
    noiseDecay: 0.18,
    noiseHz: 1200,
    ringHz: 320,
    ringGain: 0.2,
    ringDecay: 0.25,
  }),
  // A rising chime — the only cue in the game that goes up, because it is the
  // only one that is unambiguously good news.
  'airdrop:claim': voice({
    gain: 0.5,
    duration: 0.7,
    attack: 0.004,
    bodyWave: 'triangle',
    bodyFrom: 700,
    bodyTo: 1320,
    bodyGain: 0.6,
    bodyDecay: 0.4,
    noiseGain: 0,
    noiseDecay: 0.05,
    noiseHz: 4000,
    ringHz: 1760,
    ringGain: 0.25,
    ringDecay: 0.45,
  }),

  // ─── Interface ─────────────────────────────────────────────────────────────
  'ui:click': voice({
    gain: 0.28,
    duration: 0.1,
    bodyFrom: 900,
    bodyTo: 500,
    bodyGain: 0.35,
    bodyDecay: 0.04,
    noiseGain: 0.35,
    noiseDecay: 0.02,
    noiseHz: 4000,
    noiseQ: 2,
  }),
};

/** The cue a weapon archetype fires with. Total, so a new archetype is a type error. */
export function fireCue(archetype: WeaponArchetype): CueId {
  return `fire:${archetype}`;
}

/**
 * The one gun every turret currently fires with, or `null` for eight distinct
 * reports.
 *
 * **This deliberately overrides the design stated above `CUE_SHAPES`.** That
 * comment argues the eight firing cues should be different *shapes* rather than
 * one boom at eight pitches, because pitch alone does not survive three tanks
 * shooting at once. The argument still holds and the shapes are still written
 * out below — set this to `null` and the roster goes back to sounding like eight
 * different guns, in one edit.
 *
 * What it costs: a player can no longer tell a scattergun from a railgun by ear,
 * which is real information in a firefight. What it buys is that every gun gets
 * the biggest, best report in the set.
 */
export const UNIFORM_FIRE_ARCHETYPE: WeaponArchetype | null = 'rail';

/**
 * Applies `UNIFORM_FIRE_ARCHETYPE` to a table of per-archetype firing entries.
 *
 * **`duration` is kept per archetype, and that is not a detail.** The trim is
 * what stops a gun's tail outliving its own reload: the Hornet fires every
 * 0.7 s, and giving it the rail's 3.6 s tail would stack four copies of the
 * loudest sound in the game on top of each other until the voice limiter starved
 * it and the *fastest* gun became the silent one. So every turret gets the
 * rail's report, cut to fit its own cadence — the Hornet's is the rail's first
 * 0.62 s, which is the crack and none of the ring.
 */
function uniformFire<T extends { duration: number }>(
  table: Record<`fire:${WeaponArchetype}`, T>,
): Record<`fire:${WeaponArchetype}`, T> {
  if (!UNIFORM_FIRE_ARCHETYPE) return table;
  const chosen = table[fireCue(UNIFORM_FIRE_ARCHETYPE) as `fire:${WeaponArchetype}`];
  const out = { ...table };
  for (const key of Object.keys(out) as `fire:${WeaponArchetype}`[]) {
    out[key] = { ...chosen, duration: out[key].duration };
  }
  return out;
}

/**
 * The catalogue as the game plays it — `CUE_SHAPES` with the firing cues
 * collapsed onto one, if `UNIFORM_FIRE_ARCHETYPE` says so.
 *
 * Everything that is not a gun passes through untouched: impacts, the airdrops
 * and the interface are unrelated cues rather than eight versions of one.
 */
export const CUES: Record<CueId, VoiceSpec> = {
  ...CUE_SHAPES,
  ...uniformFire(
    Object.fromEntries(
      Object.entries(CUE_SHAPES).filter(([id]) => id.startsWith('fire:')),
    ) as Record<`fire:${WeaponArchetype}`, VoiceSpec>,
  ),
};

// ─── Recordings ──────────────────────────────────────────────────────────────

/**
 * The recorded half of the palette.
 *
 * Three tank-gun recordings, one breech and one seamless track loop — the whole
 * of what is not synthesised, and the whole of what a licence has to be honoured
 * for. Provenance, licence and how each was cut: `docs/audio.md`.
 *
 * **Opus in WebM, and that is load-bearing for `move-loop`.** MP3 carries
 * encoder delay and padding, so a decoded MP3 has silence welded to both ends
 * and cannot loop gaplessly however carefully it was cut. Do not re-encode.
 */
export type SampleId = 'shot-1' | 'shot-2' | 'shot-3' | 'reload' | 'move-loop' | 'impact-tank';

export const SAMPLE_IDS: readonly SampleId[] = [
  'shot-1',
  'shot-2',
  'shot-3',
  'reload',
  'move-loop',
  'impact-tank',
];

export const SAMPLE_URLS: Record<SampleId, string> = {
  'shot-1': '/assets/audio/shot-1.webm',
  'shot-2': '/assets/audio/shot-2.webm',
  'shot-3': '/assets/audio/shot-3.webm',
  reload: '/assets/audio/reload.webm',
  'move-loop': '/assets/audio/move-loop.webm',
  'impact-tank': '/assets/audio/impact-tank.webm',
};

/**
 * How long each recording actually is, in seconds.
 *
 * Stated here rather than read off the decoded buffer because it is a fact
 * about the assets that the *decisions* below depend on — a trim longer than
 * the recording is a cue that ends in silence, and that is checkable in a plain
 * test run with no audio device. `SampleBank` asserts the decoded lengths
 * against these at load time and reports any disagreement to the dev probe.
 */
export const SAMPLE_SECONDS: Record<SampleId, number> = {
  'shot-1': 3.27,
  'shot-2': 4.6,
  'shot-3': 2.3,
  reload: 0.62,
  'move-loop': 6,
  'impact-tank': 0.74,
};

/**
 * One recorded voice: which recording, how fast, how loud, and how long it is
 * allowed to sound before it is faded out.
 *
 * `duration` is *sounding* time — seconds of wall clock after `rate` has been
 * applied — for the same reason `VoiceSpec.duration` is: the limiter uses it to
 * know when the voice frees up again, so both kinds of voice have to answer the
 * same question in the same units.
 */
export interface SampleSpec {
  sample: SampleId;
  gain: number;
  /** Playback rate. Below 1 is slower, lower and therefore *bigger*. */
  rate: number;
  duration: number;
  /**
   * Seconds this voice competes for the cap — its *loud* part, not its whole
   * length.
   *
   * The one place a recorded voice is accounted for differently from a
   * synthesised one, and it needs the argument spelling out, because the rule
   * everywhere else is that a cue must not lie about how long it lasts.
   *
   * A synthesised cue's `duration` **is** its loud part: the envelope decays to
   * silence over the whole of it. A recorded gun is a transient followed by two
   * to four seconds of reverb tail that is far down on the peak within a few
   * hundred milliseconds. The cap is a ceiling on how many voices are competing
   * for the mix at once, and a tail that quiet has stopped competing long before
   * it has stopped sounding.
   *
   * Counting it anyway is not conservatism, it is a real cost: one mortar would
   * reserve a twelfth of the arena's entire audio budget for two and a half
   * seconds, and what it crowds out — because the limiter drops the newest — is
   * the impact that tells the player they have just been hit. Measured over a
   * live firefight, counting the whole tail refused **more cues than it played**.
   *
   * It never exceeds `duration`, so this can only ever free a slot early; a cue
   * cannot use it to sound for longer than it asked to.
   */
  hold: number;
  attack: number;
  /** Seconds of fade at the end, so cutting a long tail short is not a click. */
  release: number;
}

/**
 * ### Eight archetypes, three recordings
 *
 * They cannot map one to one, and the goal is not eight unrelated bangs — it is
 * **one family at eight weights**, which is exactly what the muzzle flash
 * already does. `docs/firing-effects.md` sizes its flash by
 * `cbrt(damage / 20)`: energy scales with volume, so the cube root of a damage
 * ratio is the linear scale that damage buys. The same number is used here, and
 * it buys three things.
 *
 *  - **Which recording.** Sorted by weight, the roster falls into three clean
 *    thirds — and the split lands where the *cadence* needs it to as well. The
 *    three lightest guns are also the three that stack (a scattergun's eight
 *    pellets on one tick, a quad barrel, a three-round burst), and they get
 *    `shot-3`, the shortest recording. The two heaviest are also the two with
 *    the longest reloads, and they get `shot-2`, the biggest.
 *  - **Playback rate**, as `weight ** -0.5`: a heavier gun plays slower and
 *    therefore lower. Anchored at the recruit gun's 20 damage, which is weight
 *    exactly 1 and rate exactly 1 — the recording as recorded.
 *  - **Gain**, as `weight ** 0.6`, so the rail is audibly the hardest round in
 *    the game without being 3.4 times the amplitude of a pellet.
 *
 * ### The tails are long, and that is the constraint that actually bites
 *
 * These are 2.3 s to 4.6 s of gun plus reverb, and the fastest gun in the
 * catalogue reloads in 0.7 s. A shot whose tail outlives its own reload piles up
 * on itself: by the fourth round of sustained fire there are four tails
 * sounding, the limiter starts refusing voices, and the gun that fires fastest
 * is the one that stops being heard. So every archetype states the reload of its
 * *fastest* turret, and `duration` is trimmed inside it — the recording supplies
 * the timbre, the trim supplies the cadence. `policy.test.ts` asserts the
 * inequality rather than trusting the numbers below.
 *
 * `volleySpan` is the other half of that sum: a burst's three rounds leave four
 * ticks apart, so the volley is still arriving 0.27 s after it started and the
 * budget is `duration + volleySpan`.
 */
interface FireSample {
  /**
   * Per-shell damage of this archetype's reference turret, from
   * `backend/src/garage/catalogue.data.ts`. Per *shell*, not per volley, for
   * the same reason the muzzle flash is sized per shell.
   */
  damage: number;
  sample: SampleId;
  /** Seconds this cue is allowed to sound. */
  duration: number;
  /** The shortest reload any turret of this archetype has, in seconds. */
  fastestReload: number;
  /** Seconds between the first and last shell of one trigger pull. */
  volleySpan: number;
}

/**
 * The mapping, written out.
 *
 * Read the `damage` column and the `sample` column together: the recording each
 * archetype gets is a function of nothing but its weight, and the ordering is
 * scatter < multi < burst < charge < single < ricochet < mortar < rail.
 */
export const FIRE_SAMPLES: Record<WeaponArchetype, FireSample> = {
  // ── shot-3, the 2.30 s recording: the light, fast, stacking guns ──
  /** Flak, eight pellets on one tick, 1.5 s. Trimmed to a chuff. */
  scatter: { damage: 7, sample: 'shot-3', duration: 0.75, fastestReload: 1.5, volleySpan: 0 },
  /** Twin and quad barrels. Parallel, so the whole volley leaves at once. */
  multi: { damage: 9, sample: 'shot-3', duration: 0.9, fastestReload: 1.05, volleySpan: 0 },
  /**
   * Three rounds four ticks apart, then 2.2 s. The trim is short so the burst
   * reads as three shots rather than as one smear — 0.133 s of spacing against
   * a 0.4 s tail still leaves each attack standing clear of the last.
   */
  burst: { damage: 15, sample: 'shot-3', duration: 0.4, fastestReload: 2.2, volleySpan: 0.267 },

  // ── shot-1, the 3.27 s recording: the middleweights ──
  /** Capacitor discharge. The synthesised whine still leads into it. */
  charge: { damage: 18, sample: 'shot-1', duration: 1.3, fastestReload: 1.5, volleySpan: 0 },
  /**
   * The plain gun, and the anchor of the whole scale: 20 damage is weight 1, so
   * this plays at rate 1. Its trim is the tightest in the table because the
   * *fastest* gun in the game is a `single` — the Hornet, at 0.7 s.
   */
  single: { damage: 20, sample: 'shot-1', duration: 0.62, fastestReload: 0.7, volleySpan: 0 },
  /** Bounces off the arena wall. Slightly heavier than the plain gun. */
  ricochet: { damage: 26, sample: 'shot-1', duration: 1.25, fastestReload: 1.5, volleySpan: 0 },

  // ── shot-2, the 4.60 s recording: the heavy artillery ──
  /** Arcing and area-damaging, 3.2 s between rounds. Room for a real tail. */
  mortar: { damage: 40, sample: 'shot-2', duration: 2.6, fastestReload: 3.2, volleySpan: 0 },
  /** 95 damage and four and a half seconds. The biggest noise in the game. */
  rail: { damage: 95, sample: 'shot-2', duration: 3.6, fastestReload: 4.5, volleySpan: 0 },
};

/**
 * The linear scale a shell's damage buys, anchored on the recruit gun.
 *
 * The same function `docs/firing-effects.md` sizes the muzzle flash with, and
 * deliberately so: the flash and the report are two depictions of one event, and
 * a gun that looks heavy and sounds light is worse than one that is merely
 * quiet.
 */
export function fireWeight(damage: number): number {
  if (!Number.isFinite(damage) || damage <= 0) return 1;
  return Math.cbrt(damage / 20);
}

/** Heavier plays slower, and therefore lower. Weight 1 is the recording as cut. */
export function samplePlaybackRate(weight: number): number {
  return weight ** -0.5;
}

/** Peak gain of a recorded shot before distance, stacking and the master volume. */
export const SAMPLE_BASE_GAIN = 0.5;

export function sampleFireGain(weight: number): number {
  return SAMPLE_BASE_GAIN * weight ** 0.6;
}

/**
 * Seconds a recorded shot of weight 1 competes for the voice cap.
 *
 * Scaled by weight, because a heavier gun's transient really is longer — the
 * rail's initial crack has not finished in the time the scattergun's whole chuff
 * takes. Well inside every trim in the table except the two shortest, where it
 * is the trim that wins.
 */
export const SAMPLE_HOLD_SECONDS = 0.8;

export function sampleHold(weight: number, duration: number): number {
  return Math.min(duration, SAMPLE_HOLD_SECONDS * weight);
}

/**
 * The recorded cue for each `CueId` that has one.
 *
 * Partial on purpose. Everything absent from this map — every impact, the
 * airdrops, the interface — stays synthesised, and a cue *present* here still
 * falls back to its `VoiceSpec` if the recording did not load. Nothing in the
 * game is reachable only through a file.
 */
export const SAMPLE_CUES: Partial<Record<CueId, SampleSpec>> = {
  ...(Object.fromEntries(
    (Object.entries(FIRE_SAMPLES) as [WeaponArchetype, FireSample][]).map(([archetype, choice]) => {
      // Which *recording*, and how heavy it sounds, come from the uniform
      // archetype when one is set — so every gun gets the rail's report at the
      // rail's pitch and weight. `choice` still supplies the trim, because that
      // is a property of this gun's cadence rather than of its voice.
      const voiceOf = UNIFORM_FIRE_ARCHETYPE
        ? FIRE_SAMPLES[UNIFORM_FIRE_ARCHETYPE]
        : choice;
      const weight = fireWeight(voiceOf.damage);
      return [
        fireCue(archetype),
        {
          sample: voiceOf.sample,
          gain: sampleFireGain(weight),
          rate: samplePlaybackRate(weight),
          duration: choice.duration,
          hold: sampleHold(weight, choice.duration),
          // A recorded gun has its own attack; adding one would blunt it. This
          // is a click guard, not an envelope.
          attack: 0.0015,
          // Long enough that trimming a 4.6 s tail to 0.62 s is a decay rather
          // than a gate, short enough not to eat the trim it is applied to.
          release: 0.09,
        } satisfies SampleSpec,
      ];
    }),
  ) as Record<`fire:${WeaponArchetype}`, SampleSpec>),

  /**
   * A round landing on armour — the one cue that says *I hit them*.
   *
   * Recorded, where the other two impacts stay synthesised, because this is the
   * distinction a gunner actually acts on and the synth's ring only ever
   * approximated it. `impact:ground` and `impact:wall` deliberately keep their
   * `VoiceSpec`s: they are the *contrast* this is heard against, and replacing
   * all three with recordings would flatten the one difference worth having.
   *
   * **`hold` is a third of `duration`, and that is the point of using a
   * recording here at all.** Impacts are the most frequent cue in the game —
   * every shell that lands, and a scattergun lands eight at once — and they are
   * what a wall of gun tails crowds out; the uniform rail report already cost
   * about 7% of the hits announced in the written-out battle in `policy.test.ts`.
   * The synthesised cue occupies a voice for its whole 0.5 s. This one is
   * measured: full scale at 5 ms, down 20 dB by 50 ms, 30 dB by 640 ms — so the
   * loud part is over almost at once and the slot can be freed while the tail is
   * still audibly decaying. It is strictly cheaper than what it replaces.
   */
  'impact:tank': {
    sample: 'impact-tank',
    // Below the gun reports' 0.5 base: an impact should read as an event on
    // another tank, not as a second gun going off next to the player.
    gain: 0.42,
    rate: 1,
    // Matches the synthesised cue it replaces, so swapping the two does not
    // change how long a hit is audible for.
    duration: 0.5,
    hold: 0.16,
    // A recorded impact has its own attack and it is the whole character of the
    // cue; this is a click guard, not an envelope.
    attack: 0.0015,
    release: 0.06,
  },

  /**
   * Being hit yourself — the same recording, pitched down and given room.
   *
   * The same file rather than a second one, and the two must still not sound
   * alike: *I hit them* and *I am being hit* are opposite pieces of news. Three
   * things separate them, and they are the same levers the eight guns are told
   * apart by, so this is the established idiom rather than a new trick.
   *
   *  - **Rate 0.82** — about three and a half semitones down, and a fifth
   *    longer. A round on your own hull is heard through the hull.
   *  - **Gain 0.95 against 0.42**, and `arena-scene` plays it centred with no
   *    distance rolloff, because the shell is already here.
   *  - **Duration 0.85 against 0.5**, matching the synthesised cue it replaces.
   *
   * They cannot overlap and mask each other: the `impact` branch is an if/else
   * on `victimId === localId`, so exactly one of the two plays per shell.
   *
   * `hold` is a third of the cue rather than the whole of it, for the same
   * reason as `impact:tank`, and is still far below the 0.85 s the synthesised
   * version occupied.
   */
  hit: {
    sample: 'impact-tank',
    gain: 0.95,
    rate: 0.82,
    duration: 0.85,
    hold: 0.3,
    attack: 0.0015,
    release: 0.08,
  },

  /**
   * The breech, played whole: it is 0.62 s and every gun in the game reloads
   * slower than that, so there is nothing to trim.
   */
  reload: {
    sample: 'reload',
    gain: 0.45,
    rate: 1,
    duration: SAMPLE_SECONDS.reload,
    // All transient — a breech has no tail — so it holds a slot for as long as
    // it sounds, exactly like a synthesised cue.
    hold: SAMPLE_SECONDS.reload,
    attack: 0.0015,
    release: 0.05,
  },
};

/** The recorded cue for an id, or `undefined` if that cue is synthesised. */
export function sampleFor(id: CueId): SampleSpec | undefined {
  return SAMPLE_CUES[id];
}

// ─── The track loop ──────────────────────────────────────────────────────────

/**
 * `move-loop.webm` is a **layer under the synthesised engine, not a replacement
 * for it.**
 *
 * The engine note is parametric — it answers to throttle and road speed
 * continuously, and a recording can only be played faster or slower, which
 * sounds like a tape rather than like a motor. That argument is `docs/audio.md`'s
 * and it still holds. What a recording adds is the *tracks*: link slap and
 * gravel, where volume rather than pitch carries the speed, which is precisely
 * the thing a loop is good at and a pair of oscillators is not.
 *
 * So the loop plays at a **fixed rate of 1** — no pitch shift, ever. Speed
 * reaches it through gain alone.
 */
export const TRACK_LOOP_PEAK_GAIN = 0.24;
/**
 * Fraction of top speed below which the tracks are silent.
 *
 * Not zero: a tank creeping at 2% of top speed is manoeuvring, and a rumble
 * that never quite stops reads as a stuck sound rather than as a moving one.
 */
export const TRACK_LOOP_FLOOR = 0.04;
/**
 * Seconds a track voice takes to travel most of the way to a new gain.
 *
 * On the *audio* clock, like every other continuous parameter in this feature —
 * see the smoothness invariants in `CLAUDE.md`. Slower than the engine's glide
 * because a tank crossing behind a building should fade rather than blink.
 */
export const TRACK_LOOP_GLIDE = 0.12;

/**
 * How many tanks may have a track loop at once.
 *
 * A separate budget from `MAX_VOICES`, because these are not one-shots: they
 * never end, so they can never free a slot, and letting them compete for the
 * one-shot cap would mean six moving tanks silencing the gunfire. Twelve tanks
 * is the arena maximum and six is half of it — past that they are a wash
 * anyway, and the loudest six are chosen by gain, so the ones that are dropped
 * are the far and the slow.
 */
export const MAX_TRACK_LOOPS = 6;

/**
 * Metres per second treated as "flat out" for a tank whose spec we do not have.
 *
 * Remote tanks carry no `maxForwardSpeed` in the snapshot — the wire has no
 * reason to and the simulation does not need it — so a remote's speed fraction
 * is taken against this. The roster runs from about 14 to 22 m/s, so the error
 * is a few decibels on a rumble, which is not a fact anyone can hear. The local
 * tank uses its own real top speed.
 */
export const NOMINAL_TOP_SPEED = 18;

/**
 * Gain for one tank's track loop.
 *
 * Speed enters with a slight exponent so the bottom of the range is quiet and
 * the top is not merely louder — a tank at half speed should sound like it is
 * loafing. Distance uses exactly the same rolloff every other spatial cue does,
 * so a tank you cannot hear shooting is a tank you cannot hear driving.
 */
export function trackLoopGain(speedFraction: number, distance: number): number {
  if (!Number.isFinite(speedFraction) || speedFraction <= TRACK_LOOP_FLOOR) return 0;
  const above = Math.min(1, (speedFraction - TRACK_LOOP_FLOOR) / (1 - TRACK_LOOP_FLOOR));
  return TRACK_LOOP_PEAK_GAIN * above ** 1.25 * distanceGain(distance);
}

// ─── Distance and direction ──────────────────────────────────────────────────

/** Metres inside which a sound plays at full volume. */
export const REFERENCE_DISTANCE = 14;
/**
 * Metres past which a sound is not played at all.
 *
 * The arena is 200 m across, so without a hard limit every shot fired anywhere
 * on the map would sit in the mix at a permanent whisper — twelve tanks would
 * be a wash of quiet cannon fire with nothing standing out. Cutting it off also
 * saves the voice: a cue that would be inaudible never reaches the limiter.
 */
export const MAX_AUDIBLE_DISTANCE = 160;
/**
 * How far a hard-left or hard-right source is panned.
 *
 * Short of 1 deliberately. Full-width panning on headphones puts a sound
 * *inside* one ear, which reads as a fault rather than as a direction.
 */
export const PAN_WIDTH = 0.75;

/**
 * Volume for a source `distance` metres away.
 *
 * Inverse rolloff — the honest physical shape — multiplied by a linear fade to
 * silence at the audible limit. The fade is not decoration: an inverse curve
 * alone never reaches zero, so cutting it off at a distance would leave an
 * audible step every time a tank crossed that line.
 */
export function distanceGain(distance: number): number {
  if (!Number.isFinite(distance)) return 0;
  if (distance <= REFERENCE_DISTANCE) return 1;
  if (distance >= MAX_AUDIBLE_DISTANCE) return 0;
  const inverse = REFERENCE_DISTANCE / distance;
  const fade = 1 - (distance - REFERENCE_DISTANCE) / (MAX_AUDIBLE_DISTANCE - REFERENCE_DISTANCE);
  return inverse * fade;
}

/**
 * Where a source sits in the stereo field, -1 (left) to +1 (right).
 *
 * `listenerYaw` is the direction the player is *facing*, in the same convention
 * the rest of the game uses for a heading: forward is `(sin y, cos y)`, so the
 * listener's right is `(cos y, -sin y)`. That is the same perpendicular
 * `fireLocalEffects` uses to offset a twin gun's barrels, which is what keeps a
 * shot heard on the right and a tracer drawn on the right the same side.
 */
export function stereoPan(dx: number, dz: number, listenerYaw: number): number {
  const distance = Math.hypot(dx, dz);
  if (!Number.isFinite(distance) || distance < 1e-3) return 0;
  const right = (dx * Math.cos(listenerYaw) - dz * Math.sin(listenerYaw)) / distance;
  return Math.max(-1, Math.min(1, right)) * PAN_WIDTH;
}

// ─── The voice cap ───────────────────────────────────────────────────────────

/**
 * Most one-shot voices allowed to sound at once.
 *
 * A ceiling on cost *and* on loudness. Web Audio will happily build two hundred
 * oscillators, and the result is both expensive and — because uncorrelated
 * sources sum — loud enough to clip the master bus, which is heard as
 * distortion rather than as a busy battle.
 */
export const MAX_VOICES = 12;
/**
 * Milliseconds inside which repeats of the *same* cue count as one burst.
 *
 * Sized to a simulation tick (33.3 ms) plus margin, because that is the window
 * a volley actually arrives in: a scattergun's pellets are all spawned on one
 * tick and reach the client inside one snapshot.
 */
export const COALESCE_MS = 70;
/** Repeats of one cue inside that window that still get a voice. */
export const MAX_STACK = 3;

/**
 * Gain for the nth repeat of a cue inside the coalescing window.
 *
 * Falling rather than flat, so a volley reads as *one* louder report instead of
 * n identical ones flanged against each other. A scattergun firing eight
 * pellets on one tick therefore lands as 1 + 0.55 + 0.32 ≈ 1.87 times one
 * shell's amplitude, not 8 — audibly a bigger gun, and nowhere near clipping.
 */
const STACK_GAINS = [1, 0.55, 0.32];

export function stackGain(repeat: number): number {
  return STACK_GAINS[repeat] ?? 0;
}

/** Why a cue was refused, for the dev probe. */
export type DropReason = 'stack' | 'cap';

interface ActiveVoice {
  cue: CueId;
  startedAt: number;
  endsAt: number;
}

/**
 * Decides which cues get to sound, and how loudly.
 *
 * Deliberately knows nothing about audio: it is a list of intervals and two
 * rules over them. That is what lets the scattergun case be asserted in a plain
 * test run rather than by someone putting headphones on.
 */
export class VoiceLimiter {
  private readonly active: ActiveVoice[] = [];

  played = 0;
  droppedByStack = 0;
  droppedByCap = 0;

  constructor(
    private readonly maxVoices: number = MAX_VOICES,
    private readonly coalesceMs: number = COALESCE_MS,
    private readonly maxStack: number = MAX_STACK,
  ) {}

  /**
   * Asks for a voice, and returns the gain multiplier to play it at — or 0 if
   * it must be dropped.
   *
   * Drop-newest rather than steal-oldest: a voice that has already started is
   * audible, and cutting it off mid-envelope is a click. The newest cue in an
   * overloaded moment is also the one least likely to be missed, because
   * whatever caused it is still making the other eleven noises.
   */
  request(cue: CueId, nowMs: number, durationSeconds: number): number {
    this.prune(nowMs);

    let repeats = 0;
    for (const voiceEntry of this.active) {
      if (voiceEntry.cue === cue && nowMs - voiceEntry.startedAt < this.coalesceMs) repeats += 1;
    }
    if (repeats >= this.maxStack) {
      this.droppedByStack += 1;
      return 0;
    }
    if (this.active.length >= this.maxVoices) {
      this.droppedByCap += 1;
      return 0;
    }

    this.active.push({ cue, startedAt: nowMs, endsAt: nowMs + durationSeconds * 1000 });
    this.played += 1;
    return stackGain(repeats);
  }

  /** Voices still sounding at `nowMs`. */
  activeCount(nowMs: number): number {
    this.prune(nowMs);
    return this.active.length;
  }

  private prune(nowMs: number): void {
    // In place, back to front: the array is at most `maxVoices` long, so this
    // is cheaper than rebuilding it and allocates nothing.
    for (let i = this.active.length - 1; i >= 0; i -= 1) {
      if (this.active[i].endsAt <= nowMs) this.active.splice(i, 1);
    }
  }
}
